import React, { useContext, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import products from "../data/products";
import { AppContext } from "../context/AppContext";
import { StarIcon } from '@heroicons/react/solid';
import ProductCard from "../components/ProductCard";

const ProductDetails = () => {
  const { id } = useParams();
  const { addToCart } = useContext(AppContext);
  const product = products.find((p) => p.id === parseInt(id));
  const [activeTab, setActiveTab] = useState('description');
  const [mainImage, setMainImage] = useState(0);
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-red-600">Product Not Found</h2>
      </div>
    );
  }

  const relatedProducts = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleMouseMove = (e) => {
    if (imageRef.current) {
      const { left, top, width, height } = imageRef.current.getBoundingClientRect();
      const x = ((e.pageX - left) / width) * 100;
      const y = ((e.pageY - top) / height) * 100;
      setZoomPosition({ x, y });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <div 
            className="relative overflow-hidden" 
            style={{ width: '400px', height: '400px' }}
            onMouseEnter={() => setShowZoom(true)}
            onMouseLeave={() => setShowZoom(false)}
            onMouseMove={handleMouseMove}
            ref={imageRef}
          >
            <img 
              src={product.image[mainImage]} 
              alt={product.name} 
              className="w-full h-full object-contain rounded-lg shadow-lg"
            />
            {showZoom && (
              <div 
                className="absolute top-0 left-full ml-4 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                style={{ width: '400px', height: '400px' }}
              >
                <img 
                  src={product.image[mainImage]} 
                  alt={product.name} 
                  className="absolute w-[800px] h-[800px] max-w-none"
                  style={{
                    transform: `translate(-${zoomPosition.x}%, -${zoomPosition.y}%)`,
                  }}
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-5 gap-2 mt-4">
            {product.image.map((img, index) => (
              <img 
                key={index}
                src={img} 
                alt={`${product.name} - ${index + 1}`}
                className={`w-full h-20 object-cover rounded cursor-pointer ${mainImage === index ? 'border-2 border-blue-500' : ''}`}
                onClick={() => setMainImage(index)}
              />
            ))}
          </div>
        </div>
        <div>
          <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
          <div className="flex items-center mb-4">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} className="h-5 w-5" />
              ))}
            </div>
            <span className="ml-2 text-gray-600">({product.reviews.length} reviews)</span>
          </div>
          <p className="text-2xl font-semibold text-blue-600 mb-4">{product.price} {product.currency}</p>
          <p className="text-gray-600 mb-4">Category: {product.category}</p>
          <p className="text-gray-700 mb-6">{product.description}</p>
          <button 
            onClick={() => addToCart(product)} 
            className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition duration-300 mb-6"
          >
            Add to Cart
          </button>
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">Product Specifications</h2>
            <ul className="list-disc list-inside text-gray-700">
              {product.specifications.map((spec, index) => (
                <li key={index}>{spec}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {['description', 'reviews'].map((tab) => (
              <button
                key={tab}
                className={`${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-8">
          {activeTab === 'description' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Product Description</h2>
              <p className="text-gray-700">{product.description}</p>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Customer Reviews</h2>
              {product.reviews.map((review) => (
                <div key={review.id} className="mb-4 pb-4 border-b border-gray-200 last:border-b-0">
                  <div className="flex items-center mb-2">
                    <div className="flex text-yellow-400">
                      {[...Array(review.rating)].map((_, i) => (
                        <StarIcon key={i} className="h-5 w-5" />
                      ))}
                    </div>
                    <span className="ml-2 font-semibold">{review.author}</span>
                  </div>
                  <p className="text-gray-700">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Related Products */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Related Products</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {relatedProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;